/**
 * Pure geometry helpers for the mask editor (no DOM / no canvas).
 *
 * The mask editor renders the source image in a viewport that can be zoomed
 * and panned. Painting must be recorded in *image-space* coordinates (so the
 * exported mask matches the uploaded image exactly, regardless of zoom/pan).
 * These helpers convert screen-space (pointer events relative to the viewport
 * top-left) into image-space, and check whether the mask has any painted pixels.
 *
 * View model: an image pixel (ix, iy) maps to viewport coords via
 *   screenX = panX + ix * scale
 *   screenY = panY + iy * scale
 * where `scale` is pixels-per-image-pixel (baseScale * zoom) and (panX, panY)
 * is the viewport-space position of the image origin (top-left). transformOrigin
 * is the top-left, so this stays a simple affine map.
 */

export interface Point {
  x: number;
  y: number;
}

export interface ViewTransform {
  /** Combined pixels-per-image-pixel (baseScale * zoom). Must be > 0. */
  scale: number;
  /** Viewport-space x of the image origin (top-left). */
  panX: number;
  /** Viewport-space y of the image origin (top-left). */
  panY: number;
}

/**
 * Convert a screen-space point (relative to the viewport top-left) into
 * image-space coordinates under the current view transform.
 */
export function screenToImage(screen: Point, view: ViewTransform): Point {
  return {
    x: (screen.x - view.panX) / view.scale,
    y: (screen.y - view.panY) / view.scale,
  };
}

/**
 * Fit (contain) scale for an image inside a viewport: the largest scale that
 * keeps the whole image visible. Returns 1 for degenerate inputs.
 */
export function computeFitScale(
  imageWidth: number,
  imageHeight: number,
  viewportWidth: number,
  viewportHeight: number
): number {
  if (imageWidth <= 0 || imageHeight <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
    return 1;
  }
  return Math.min(viewportWidth / imageWidth, viewportHeight / imageHeight);
}

/**
 * Forward projection: image-space point -> screen-space (viewport top-left origin).
 * Inverse of {@link screenToImage}. Used to position the screen-space transform gizmo
 * (the placed-image move/resize box) so its handles stay a constant on-screen size
 * regardless of zoom.
 */
export function imageToScreen(image: Point, view: ViewTransform): Point {
  return {
    x: image.x * view.scale + view.panX,
    y: image.y * view.scale + view.panY,
  };
}

/** An axis-aligned rectangle in image-space pixels. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Which corner of a {@link Rect} is being dragged. */
export type Corner = 'nw' | 'ne' | 'sw' | 'se';

/**
 * Contain-fit a natural image size into a box, preserving aspect ratio. Returns the
 * fitted width/height (the caller centers it). Used to give a freshly added image-mask
 * layer a sensible default size. Returns {0,0} for degenerate inputs.
 */
export function fitContainRect(
  naturalWidth: number,
  naturalHeight: number,
  boxWidth: number,
  boxHeight: number
): { w: number; h: number } {
  if (naturalWidth <= 0 || naturalHeight <= 0 || boxWidth <= 0 || boxHeight <= 0) {
    return { w: 0, h: 0 };
  }
  const scale = Math.min(boxWidth / naturalWidth, boxHeight / naturalHeight);
  return { w: naturalWidth * scale, h: naturalHeight * scale };
}

/** Per-corner sign of the dragged corner relative to the rect center (x,y ∈ {-1,+1}). */
function cornerSign(corner: Corner): { sx: number; sy: number } {
  switch (corner) {
    case 'nw':
      return { sx: -1, sy: -1 };
    case 'ne':
      return { sx: 1, sy: -1 };
    case 'sw':
      return { sx: -1, sy: 1 };
    case 'se':
      return { sx: 1, sy: 1 };
  }
}

/** `base + R(angle)·v` — translate `base` by the vector `v` rotated by `angle` (radians). */
function addRotated(base: Point, v: Point, angle: number): Point {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: base.x + v.x * cos - v.y * sin, y: base.y + v.x * sin + v.y * cos };
}

/**
 * Rotate a point around `center` by `angle` (radians; canvas axes — +x right, +y down,
 * so a positive angle turns clockwise on screen). Pure — no DOM.
 */
export function rotatePoint(p: Point, center: Point, angle: number): Point {
  return addRotated(center, { x: p.x - center.x, y: p.y - center.y }, angle);
}

/** Snap `angle` (radians) to the nearest multiple of `step`. A `step <= 0` is a no-op. */
export function snapAngle(angle: number, step: number): number {
  if (step <= 0) return angle;
  return Math.round(angle / step) * step;
}

/** Options for {@link resizeRotatedRectFromCorner}. */
export interface ResizeOptions {
  /** Lower bound for each dimension (image-space px). Defaults to 1. */
  minSize?: number;
  /** When true (default) keep the rect's aspect ratio; when false allow free stretch. */
  lockAspect?: boolean;
}

/**
 * Turn the raw drag extents (anchor→pointer, in the rect's unrotated local frame) into a
 * final width/height. Aspect-locked: grow along the dominant axis then derive the other,
 * clamping up to `minSize` while preserving aspect. Free: each axis independent, each
 * clamped to `minSize`.
 */
function sizeFromRaw(
  rawW: number,
  rawH: number,
  aspect: number,
  minSize: number,
  lockAspect: boolean
): { w: number; h: number } {
  if (!lockAspect) {
    return { w: Math.max(rawW, minSize), h: Math.max(rawH, minSize) };
  }
  let w = Math.max(rawW, rawH * aspect);
  let h = aspect !== 0 ? w / aspect : rawH;
  // Clamp up so neither dimension drops below minSize, preserving aspect. A degenerate drag
  // (pointer on the anchor → w/h collapse to 0) leaves the ratio undefined, so emit the
  // smallest aspect-correct box directly instead of dividing by zero.
  if (w > 0 && h > 0) {
    const up = Math.max(minSize / w, minSize / h, 1);
    if (up > 1) {
      w *= up;
      h *= up;
    }
  } else {
    const a = aspect > 0 ? aspect : 1;
    w = a >= 1 ? minSize * a : minSize;
    h = a >= 1 ? minSize : minSize / a;
  }
  return { w, h };
}

/**
 * Corner resize for a rect rotated by `angle` (radians) about its center. The corner
 * opposite `corner` stays fixed in *world* (image) space — same feel as the unrotated
 * editor — while the dragged corner follows `pointer`. The math runs in the rect's
 * unrotated local frame: project the anchor→pointer vector back by `-angle`, size it (see
 * {@link sizeFromRaw}), then place the new center so the anchor corner doesn't move.
 * `angle = 0` reduces exactly to {@link resizeRectFromCorner}. Pure — no DOM.
 */
export function resizeRotatedRectFromCorner(
  rect: Rect,
  angle: number,
  corner: Corner,
  pointer: Point,
  opts: ResizeOptions = {}
): Rect {
  const minSize = opts.minSize ?? 1;
  const lockAspect = opts.lockAspect ?? true;
  const aspect = rect.h !== 0 ? rect.w / rect.h : 1;

  const center: Point = { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
  const sign = cornerSign(corner);

  // World position of the opposite (anchor) corner — the point that stays put.
  const anchorWorld = addRotated(center, { x: (-sign.sx * rect.w) / 2, y: (-sign.sy * rect.h) / 2 }, angle);

  // anchor→pointer, expressed back in the rect's unrotated local axes (R(-angle)·v).
  const d = addRotated({ x: 0, y: 0 }, { x: pointer.x - anchorWorld.x, y: pointer.y - anchorWorld.y }, -angle);
  const { w, h } = sizeFromRaw(Math.abs(d.x), Math.abs(d.y), aspect, minSize, lockAspect);

  // Keep the anchor corner fixed: the new center sits half a box from it along the dragged diagonal.
  const newCenter = addRotated(anchorWorld, { x: (sign.sx * w) / 2, y: (sign.sy * h) / 2 }, angle);
  return { x: newCenter.x - w / 2, y: newCenter.y - h / 2, w, h };
}

/**
 * Aspect-locked corner resize (unrotated). Thin wrapper over
 * {@link resizeRotatedRectFromCorner} at `angle = 0`. Pure — no DOM.
 */
export function resizeRectFromCorner(
  rect: Rect,
  corner: Corner,
  pointer: Point,
  minSize = 1
): Rect {
  return resizeRotatedRectFromCorner(rect, 0, corner, pointer, { minSize, lockAspect: true });
}

/** Minimal slot shape {@link itemSupportsMask} reads. */
export interface MaskSlotLike {
  mediaType?: string;
  enableMask?: boolean;
}

/** Minimal slot-item shape {@link itemSupportsMask} reads. */
export interface MaskItemLike {
  mediaType?: string;
  dimensions?: unknown;
  processing?: boolean;
}

/**
 * Whether a given slot item may carry an inpainting mask. True only for the slot's FIRST item
 * (`itemIndex === 0`) of a mask-enabled image slot, when that item is a ready (non-processing)
 * image with known dimensions. The first-item rule binds the single per-slot mask to
 * `image_urls[0]` — the only image gpt-image-2 edit applies a mask to — so any later image in a
 * multi-image slot is crop-only. Centralizes the per-site `canMask` checks in the Big slots widget.
 */
export function itemSupportsMask(slot: MaskSlotLike, item: MaskItemLike, itemIndex: number): boolean {
  return (
    itemIndex === 0 &&
    slot.mediaType === 'image' &&
    !!slot.enableMask &&
    item.mediaType === 'image' &&
    !!item.dimensions &&
    !item.processing
  );
}

/**
 * True if any pixel in RGBA `data` has a non-zero alpha — i.e. the mask has
 * something painted. Used to decide an "empty" mask (nothing painted, or a
 * seed/strokes fully erased) so the caller omits the payload key entirely.
 */
export function hasOpaquePixels(data: Uint8ClampedArray): boolean {
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] !== 0) return true;
  }
  return false;
}

/** Default payload key for an inpainting mask when a slot leaves it unset. */
export const DEFAULT_MASK_PAYLOAD_KEY = 'mask_image';

/**
 * Reserved internal marker key. `getPayloadData` appends this to the payload to tell the
 * backend which keys carry a mask (so masks stay out of the generic `image_urls` bucket);
 * both backends then strip it. A slot_id or mask key equal to this would be clobbered by
 * (or clobber) the marker and then deleted server-side — silent data loss — so it is
 * treated as a collision. Kept in sync with the literal both backends delete.
 */
export const MASK_PAYLOAD_KEYS_MARKER = '__mask_payload_keys';

/**
 * Payload keys the slots-input-media widget writes for media counters / dimensions (besides
 * the per-slot slot_id keys). A mask key equal to one of these collides in the runtime
 * payload: the counter already occupies `data[key]`, so `getPayloadData` silently skips the
 * mask write while its File still rides in `requiredFiles` — corrupting the requiredFiles↔data
 * ordering. Both the submit-time collision guard (`validateState`) and the admin display nudge
 * (`maskPayloadKeyCollides`) must treat these as occupied. Kept in sync with the counter
 * assignments in `BigSlotsInputMediaWidget.getPayloadData`.
 */
export const RESERVED_COUNTER_PAYLOAD_KEYS: readonly string[] = [
  'audio_duration',
  'video_duration',
  'audio_duration_exact',
  'video_duration_exact',
  'slots_image_count',
  'slots_video_count',
  'slots_audio_count',
  'slots_model_count',
  'video_width',
  'video_height',
  'video_mpx',
  'video_fps',
  'video_frame_count',
  'video_size_bytes',
];

/**
 * Resolve the effective mask payload key for a slot. A blank/whitespace key falls
 * back to the default so a painted mask is never silently dropped (the admin field is
 * only a soft nudge, not a hard validation). Both the payload assembler and the submit
 * guard must resolve the key the same way, or they disagree about collisions.
 */
export function resolveMaskPayloadKey(maskPayloadKey?: string): string {
  return maskPayloadKey?.trim() || DEFAULT_MASK_PAYLOAD_KEY;
}

/**
 * Keys that appear more than once in `keys` (each duplicate reported once, in first-seen
 * order). Two masked slots resolving to the same payload key collide in the submitted
 * payload: `data[key]` is overwritten by the last slot while every mask File is still
 * pushed to `requiredFiles`, breaking the requiredFiles↔data-key ordering the backend
 * relies on. The blank-key fallback makes this trivial to hit, so the caller blocks submit
 * on a non-empty result.
 */
export function findDuplicateMaskPayloadKeys(keys: string[]): string[] {
  const counts = new Map<string, number>();
  for (const key of keys) counts.set(key, (counts.get(key) || 0) + 1);
  const duplicates: string[] = [];
  counts.forEach((count, key) => {
    if (count > 1) duplicates.push(key);
  });
  return duplicates;
}

/**
 * The full set of payload keys a mask key must never collide with, given the slot_ids
 * configured on a slots-input-media widget. A mask key equal to any of these is unusable:
 *
 *  - a `slot_id` (any slot's, its own included) — the server's `getConfiguredMaskPayloadKeys`
 *    drops such a key from the authorized mask-key set, so the mask is never stripped from
 *    `image_urls` and (on single_per_request) is billed as its own request. CRITICAL: this
 *    must include EMPTY slots too. The server reserves every configured slot_id regardless of
 *    whether that slot is filled in the current request, so the client must as well — otherwise
 *    a stale/imported config whose mask key matches an empty sibling slot's id would emit a
 *    mask the server then refuses to recognize (see getPayloadData / validateState callers).
 *  - a reserved media-counter key ({@link RESERVED_COUNTER_PAYLOAD_KEYS}) — the counter occupies
 *    `data[key]`, clobbering the mask (or being clobbered).
 *  - the reserved {@link MASK_PAYLOAD_KEYS_MARKER}.
 *
 * Mirrors the server-side `occupied` set in `getConfiguredMaskPayloadKeys` so the client never
 * emits — nor accepts at submit — a mask under a key the server won't honor.
 */
export function getMaskCollisionKeys(slotIds: Iterable<string>): Set<string> {
  const occupied = new Set<string>([MASK_PAYLOAD_KEYS_MARKER, ...RESERVED_COUNTER_PAYLOAD_KEYS]);
  for (const id of slotIds) {
    if (id) occupied.add(id);
  }
  return occupied;
}

/**
 * Mask keys that collide with a key already occupied by something else in the payload —
 * an ordinary slot_id, a media counter ({@link RESERVED_COUNTER_PAYLOAD_KEYS}), or the
 * reserved {@link MASK_PAYLOAD_KEYS_MARKER}. Such a collision overwrites an earlier payload
 * entry (or is itself clobbered) while the mask File still rides in `requiredFiles`, breaking
 * the requiredFiles↔data-key ordering the backend's sequential blob replacement relies on
 * (wrong URL bound + a stray mask URL left in `image_urls`) — or the mask is silently dropped
 * because `getPayloadData` skips a key already present in `data`. The marker and the counter
 * keys are reserved unconditionally; the caller passes the dynamic occupied keys (slot_ids)
 * via `occupiedKeys` — and must pass ALL configured slot_ids, not just currently-filled ones,
 * to match the server (see {@link getMaskCollisionKeys}). Each colliding key is reported once
 * in first-seen order; the caller blocks submit on a non-empty result. (This is distinct from
 * {@link findDuplicateMaskPayloadKeys}, which only covers mask-vs-mask collisions.)
 */
export function findCollidingMaskPayloadKeys(
  maskKeys: string[],
  occupiedKeys: Iterable<string>
): string[] {
  const occupied = getMaskCollisionKeys(occupiedKeys);
  const seen = new Set<string>();
  const collisions: string[] = [];
  for (const key of maskKeys) {
    if (occupied.has(key) && !seen.has(key)) {
      seen.add(key);
      collisions.push(key);
    }
  }
  return collisions;
}
